import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SignaturePad from "signature_pad";

/** Strip the `data:image/png;base64,` prefix — the loan APIs want raw base64. */
const toRawBase64 = (dataUrl) => String(dataUrl || "").replace(/^data:.*?;base64,/, "");

/**
 * Dashed box the user draws a signature into. Emits the drawn signature as raw
 * base64 PNG through `onChange`, or `null` once cleared.
 *
 * Wraps `signature_pad` directly — react-signature-canvas has no stable release
 * for React 19.
 */
const SignaturePadField = ({
  title,
  helper,
  emptyHint = "Draw your signature in the box above",
  height = 220,
  disabled = false,
  onChange,
}) => {
  const canvasRef = React.useRef(null);
  const padRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  const [hasSignature, setHasSignature] = React.useState(false);

  // Keep the latest callback without re-running the setup effect.
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /** Size the backing store to the device pixel ratio so strokes stay crisp. */
  const resizeCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    const pad = padRef.current;
    if (!canvas || !pad) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const { width } = canvas.getBoundingClientRect();
    if (!width) return;
    // Resizing clears the canvas, so restore whatever has been drawn.
    const previous = pad.isEmpty() ? null : pad.toData();
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.getContext("2d").scale(ratio, ratio);
    pad.clear();
    if (previous) pad.fromData(previous);
  }, [height]);

  React.useEffect(() => {
    // The canvas may mount inside a dialog, so set up after paint.
    const frame = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const pad = new SignaturePad(canvas, {
        penColor: "#020617",
        backgroundColor: "rgba(0,0,0,0)",
        minWidth: 0.8,
        maxWidth: 2.2,
      });
      pad.addEventListener("endStroke", () => {
        const empty = pad.isEmpty();
        setHasSignature(!empty);
        onChangeRef.current?.(empty ? null : toRawBase64(pad.toDataURL("image/png")));
      });
      padRef.current = pad;
      resizeCanvas();
    });

    window.addEventListener("resize", resizeCanvas);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resizeCanvas);
      padRef.current?.off();
      padRef.current = null;
    };
  }, [resizeCanvas]);

  React.useEffect(() => {
    const pad = padRef.current;
    if (!pad) return;
    if (disabled) pad.off();
    else pad.on();
  }, [disabled]);

  const handleClear = () => {
    padRef.current?.clear();
    setHasSignature(false);
    onChangeRef.current?.(null);
  };

  return (
    <Box>
      {(title || !disabled) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={title ? "space-between" : "flex-end"}
          spacing={2}
        >
          {title ? (
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.strong" }}>
              {title}
            </Typography>
          ) : null}
          <Button
            onClick={handleClear}
            disabled={disabled || !hasSignature}
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 0,
              px: 0.5,
              fontSize: 15,
              fontWeight: 600,
              textTransform: "none",
              color: "text.strong",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            Clear
          </Button>
        </Stack>
      )}

      {helper ? (
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
          {helper}
        </Typography>
      ) : null}

      <Box
        sx={{
          mt: 1.5,
          p: 1,
          borderRadius: 1.5,
          border: (theme) => `2px dashed ${theme.palette.border.light}`,
          bgcolor: "background.subtle",
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            display: "block",
            width: "100%",
            height,
            touchAction: "none",
            cursor: disabled ? "not-allowed" : "crosshair",
          }}
        />
        <Stack alignItems="center" spacing={0.5} sx={{ pb: 1.5, minHeight: 56 }}>
          {hasSignature ? (
            <>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 26, color: (theme) => theme.palette.credit.score }}
              />
              <Typography sx={{ fontSize: 15, color: "text.secondary" }}>
                Signature captured successfully
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: "text.faint" }}>
              {emptyHint}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default SignaturePadField;
